import * as PIXI from 'pixi.js';
import { Collider } from './Collider';
import { getItemConfig } from '@/config/ItemsConfig';

export interface ItemInstanceConfig {
  x: number;
  y: number;
  itemId: string; // References ItemsConfig
  quantity?: number; // Default to 1 if not specified
  size?: number;
  interactionRadius?: number;
  colliderVisible?: boolean;
  dialog : string;
}

export class Item {
  public sprite: PIXI.Container;
  private itemSprite: PIXI.Sprite | null = null;
  private graphics: PIXI.Graphics;
  public collider: Collider;
  private interactionIndicator: PIXI.Graphics;

  public x: number;
  public y: number;
  public itemId: string;
  public quantity: number;
  public size: number;
  public interactionRadius: number;
  private isPlayerNearby: boolean = false;
  private isFocused: boolean = false;
  private onInteractionClick?: () => void;

  constructor(config: ItemInstanceConfig) {
    this.x = config.x;
    this.y = config.y;
    this.itemId = config.itemId;
    this.quantity = config.quantity || 1;
    this.size = config.size || 30;
    this.interactionRadius = config.interactionRadius || 50;

    // Get item configuration
    const itemConfig = getItemConfig(this.itemId);
    if (!itemConfig) {
      console.warn(`Item config not found for itemId: ${this.itemId}`);
    }

    // Create main container for item sprite
    this.sprite = new PIXI.Container();
    this.sprite.x = this.x;
    this.sprite.y = this.y;
    this.sprite.zIndex = 1; // Items should be at same level as NPCs

    // Create graphics fallback
    this.graphics = new PIXI.Graphics();
    this.graphics.zIndex = 0;

    // Try to load item sprite
    if (itemConfig?.imageUrl) {
      try {
        const texture = PIXI.Texture.from(itemConfig.imageUrl);
        this.itemSprite = new PIXI.Sprite(texture);
        this.itemSprite.anchor.set(0.5);
        this.itemSprite.zIndex = 0;

        // Scale sprite to fit size
        const scale = this.size / Math.max(this.itemSprite.width, this.itemSprite.height);
        this.itemSprite.scale.set(scale);

        this.sprite.addChild(this.itemSprite);
      } catch (error) {
        console.warn(`Failed to load item sprite: ${itemConfig.imageUrl}`, error);
        this.createFallbackGraphics();
      }
    } else {
      this.createFallbackGraphics();
    }

    // Create collider for the item
    this.collider = new Collider({
      x: this.x - this.size / 2,
      y: this.y - this.size / 2,
      width: this.size,
      height: this.size,
      color: 0xffaa00, // Orange color for items
      visible: config.colliderVisible ?? false,
    });

    // Adjust collider sprite position to be relative to item container
    this.collider.sprite.x = -this.size / 2;
    this.collider.sprite.y = -this.size / 2;

    // Create interaction indicator (sparkle effect)
    this.interactionIndicator = new PIXI.Graphics();
    this.createInteractionIndicator();
    this.interactionIndicator.visible = false;
    this.interactionIndicator.zIndex = 10;

    // Make the interaction indicator interactive
    this.interactionIndicator.eventMode = 'static';
    this.interactionIndicator.cursor = 'pointer';
    this.interactionIndicator.on('pointerdown', () => {
      if (this.onInteractionClick) {
        this.onInteractionClick();
      }
    });

    // Add elements to sprite container
    this.sprite.addChild(this.interactionIndicator);
    this.sprite.addChild(this.collider.sprite);
  }

  private createFallbackGraphics(): void {
    // Draw a simple treasure chest icon as fallback
    this.graphics.clear();
    this.graphics.beginFill(0xffd700); // Gold color
    this.graphics.drawRect(-this.size / 2, -this.size / 2, this.size, this.size);
    this.graphics.endFill();

    // Draw a simple "?" symbol
    this.graphics.lineStyle(2, 0x000000);
    this.graphics.arc(0, -4, 4, -0.5 * Math.PI, 0.5 * Math.PI, false);
    this.graphics.lineTo(0, 0);
    this.graphics.lineStyle(0);

    this.graphics.beginFill(0x000000);
    this.graphics.drawCircle(0, 4, 2);
    this.graphics.endFill();

    this.sprite.addChild(this.graphics);
  }

  private createInteractionIndicator(): void {
    this.interactionIndicator.clear();

    // Create a sparkle/star effect
    const sparkleSize = 16;
    const points: number[] = [];

    // Create 4-pointed star
    for (let i = 0; i < 8; i++) {
      const angle = (i * Math.PI) / 4;
      const radius = i % 2 === 0 ? sparkleSize : sparkleSize / 2;
      points.push(Math.cos(angle) * radius, Math.sin(angle) * radius);
    }

    this.interactionIndicator.beginFill(0xffff00, 0.8); // Yellow sparkle
    this.interactionIndicator.drawPolygon(points);
    this.interactionIndicator.endFill();

    // Position above the item
    this.interactionIndicator.y = -this.size / 2 - 20;
  }

  public update(_deltaTime: number, playerPosition: { x: number; y: number }): void {
    // Calculate distance to player
    const dx = playerPosition.x - this.x;
    const dy = playerPosition.y - this.y;
    const distance = Math.sqrt(dx * dx + dy * dy);

    // Update if player is nearby
    this.isPlayerNearby = distance <= this.interactionRadius;

    // Only show interaction indicator if both nearby AND focused
    const shouldShowIndicator = this.isPlayerNearby && this.isFocused;

    if (this.interactionIndicator.visible !== shouldShowIndicator) {
      this.interactionIndicator.visible = shouldShowIndicator;
    }

    // Add floating animation to interaction indicator
    if (shouldShowIndicator) {
      const time = Date.now() * 0.005;
      const floatOffset = Math.sin(time) * 3;
      this.interactionIndicator.y = -this.size / 2 - 20 + floatOffset;

      // Rotation animation
      this.interactionIndicator.rotation = Math.sin(time * 0.8) * 0.3;
    }
  }

  public setFocused(focused: boolean): void {
    this.isFocused = focused;
  }

  public getFocused(): boolean {
    return this.isFocused;
  }

  public canInteract(playerPosition: { x: number; y: number }): boolean {
    const dx = playerPosition.x - this.x;
    const dy = playerPosition.y - this.y;
    const distance = Math.sqrt(dx * dx + dy * dy);
    return distance <= this.interactionRadius;
  }

  public getPosition(): { x: number; y: number } {
    return { x: this.x, y: this.y };
  }

  public getCollider(): Collider {
    return this.collider;
  }

  public setColliderVisible(visible: boolean): void {
    this.collider.setVisible(visible);
  }

  public setOnInteractionClick(callback: () => void): void {
    this.onInteractionClick = callback;
  }

  public destroy(): void {
    this.sprite.destroy({ children: true });
  }
}
